import {useCallback, useEffect, useMemo, useState} from 'react';
import {standardizeText} from '../helpers';
import memoizedCategories from '../memoizedCategories';

export const useFilters = (allCabinets) => {
    // Load persisted filters
    const [selectedFilters, setSelectedFilters] = useState(() => {
        const stored = localStorage.getItem('showroomSelectedFilters');
        try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) return parsed;
        } catch {
        }
        return [];
    });

    // Load persisted expansions (top-level & nested)
    const [expandedCategories, setExpandedCategories] = useState(() => {
        const stored = localStorage.getItem('showroomExpandedCategories');
        try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) return parsed;
        } catch {
        }
        return [];
    });

    // Persist filters on change
    useEffect(() => {
        localStorage.setItem('showroomSelectedFilters', JSON.stringify(selectedFilters));
    }, [selectedFilters]);

    // Persist expansions on change
    useEffect(() => {
        localStorage.setItem('showroomExpandedCategories', JSON.stringify(expandedCategories));
    }, [expandedCategories]);

    // On mount: if we have filters but no expansions, auto-expand every panel (top & nested) containing a selected filter
    useEffect(() => {
        if (selectedFilters.length > 0 && expandedCategories.length === 0) {
            const toExpand = [];

            memoizedCategories.forEach(cat => {
                // Check if this top-level category has any selected filter
                const hasInTop = cat.subcategories.some(sub => {
                    // Leaf subcategory
                    if (sub.tags) {
                        return sub.tags.some(tag => selectedFilters.includes(tag));
                    }
                    // Nested group
                    return sub.subcategories.some(nested =>
                        nested.tags.some(tag => selectedFilters.includes(tag))
                    );
                });
                if (hasInTop) {
                    // Expand the top-level category
                    toExpand.push(cat.category);

                    // Now dive into nested subcategories
                    cat.subcategories.forEach(sub => {
                        if (sub.subcategories) {
                            const hasInNested = sub.subcategories.some(nested =>
                                nested.tags.some(tag => selectedFilters.includes(tag))
                            );
                            if (hasInNested) {
                                // Expand this nested panel too
                                toExpand.push(sub.category);
                            }
                        }
                    });
                }
            });

            if (toExpand.length) {
                // Dedupe and set
                setExpandedCategories(Array.from(new Set(toExpand)));
            }
        }
    }, []);

    // Toggle a filter on/off
    const handleFilterSelection = useCallback(filter => {
        setSelectedFilters(prev => {
            const preset = memoizedCategories.find(c => c.category === 'Preset Styles');
            if (preset) {
                const presetTags = preset.subcategories.flatMap(sub =>
                    sub.subcategories ? sub.subcategories.flatMap(n => n.tags) : sub.tags
                );
                if (presetTags.includes(filter)) {
                    // If selecting a preset, clear other presets first
                    const without = prev.filter(f => !presetTags.includes(f));
                    return prev.includes(filter) ? without : [...without, filter];
                }
            }
            // Normal toggle
            return prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter];
        });
    }, []);

    // Toggle expand/collapse by category name (works for top & nested)
    const handleCategoryClick = useCallback(category => {
        setExpandedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
    }, []);

    // Helpers to drive your panel rendering
    const hasCabinetsWithFilterTag = useCallback(
        filterTag =>
            allCabinets.some(cab =>
                cab.filterTags.split(',').map(t => t.trim()).includes(filterTag)
            ),
        [allCabinets]
    );
    const isFilterSelected = useCallback(filter => selectedFilters.includes(filter), [
        selectedFilters
    ]);
    const isCategoryExpanded = useCallback(category => expandedCategories.includes(category), [
        expandedCategories
    ]);

    return {
        selectedFilters,
        setSelectedFilters,
        handleFilterSelection,
        handleCategoryClick,
        hasCabinetsWithFilterTag,
        isFilterSelected,
        isCategoryExpanded
    };
};

export const useFilteredCabinets = (
    allCabinets,
    selectedFilters,
    searchQuery,
    isFirstLoad,
    randomizedCabinets
) => {
    return useMemo(() => {
        if (selectedFilters.length === 0 && !searchQuery) {
            return isFirstLoad ? randomizedCabinets : allCabinets;
        }

        const typeAndFunctionFilters = selectedFilters.filter(f =>
            memoizedCategories[0].subcategories.some(sub => sub.tags.includes(f))
        );
        const featureFilters = selectedFilters.filter(f => !typeAndFunctionFilters.includes(f));
        const featureCategories = memoizedCategories.slice(1);

        const terms = standardizeText(searchQuery.toLowerCase())
            .split(/\s+/)
            .filter(t => t);

        return allCabinets.filter(cabinet => {
            const tags = cabinet.filterTags
                .split(',')
                .map(t => t.trim().toLowerCase());

            const matchesType =
                typeAndFunctionFilters.length === 0 ||
                typeAndFunctionFilters.some(f => tags.includes(f.toLowerCase()));

            const matchesFeatures = featureCategories.every(cat => {
                const catFilters = featureFilters.filter(f =>
                    cat.subcategories.some(sub =>
                        sub.subcategories
                            ? sub.subcategories.some(n => n.tags.includes(f))
                            : sub.tags.includes(f)
                    )
                );
                return catFilters.length === 0 || catFilters.some(f => tags.includes(f.toLowerCase()));
            });

            if (!searchQuery) return matchesType && matchesFeatures;

            const matchesSerial = cabinet.serialNumber
                .toLowerCase()
                .includes(searchQuery.toLowerCase());
            const desc = standardizeText(cabinet.description.toLowerCase());
            const matchesDesc = terms.every(term => desc.includes(term));

            return matchesType && matchesFeatures && (matchesSerial || matchesDesc);
        });
    }, [allCabinets, selectedFilters, searchQuery, isFirstLoad, randomizedCabinets]);
};
